/**
 * Vessels Tab - Client Admin Panel
 *
 * Display and manage vessels in user's organization.
 * Phase 1: Dummy data only ✅
 * Phase 2: Real data with loading/error/empty states ✅
 * Phase 3: CRUD operations (create, edit, delete) ✅
 */

import React, { useState } from 'react';
import { Ship, Loader2, AlertCircle, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useClientVessels } from '../../../hooks/client/useClientVessels';
import { usePermissions } from '../../../hooks/usePermissions';

// Flag State options (common maritime flags)
const FLAG_STATE_OPTIONS = [
  'United Kingdom',
  'Cayman Islands',
  'Isle of Man',
  'Gibraltar',
  'Bahamas',
  'Marshall Islands',
  'Panama',
  'Liberia',
  'Malta',
  'Cyprus',
  'Other'
];

// Vessel Type options (WBC3 categories)
const VESSEL_TYPE_OPTIONS = [
  'Workboat',
  'Crew Transfer Vessel',
  'Passenger Vessel',
  'Fishing Vessel',
  'Cargo Vessel',
  'Pilot Vessel',
  'Survey Vessel',
  'Dive Support Vessel',
  'Tug',
  'Other'
];

const VesselsTab = () => {
  // Phase 2: Real data from Supabase
  const { currentUser, isFleet } = usePermissions();
  const organizationId = currentUser?.organizationId;
  const { vessels, isLoading, error, refresh, createVessel, updateVessel, deleteVessel } = useClientVessels(organizationId);

  // Phase 3: State for CRUD operations
  const [showModal, setShowModal] = useState(false);
  const [editingVessel, setEditingVessel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    imo_number: '',
    flag_state: '',
    vessel_type: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Permission check: Fleet role (OWNER/ADMIN) can edit
  const hasEditPermission = isFleet;

  // Handlers
  const handleOpenCreate = () => {
    setEditingVessel(null);
    setFormData({ name: '', imo_number: '', flag_state: '', vessel_type: '' });
    setValidationErrors({});
    setShowModal(true);
  };

  const handleOpenEdit = (vessel) => {
    setEditingVessel(vessel);
    setFormData({
      name: vessel.name,
      imo_number: vessel.imo_number || '',
      flag_state: vessel.flag_state || '',
      vessel_type: vessel.vessel_type || ''
    });
    setValidationErrors({});
    setShowModal(true);
  };

  // Client-side validation
  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Vessel name is required';
    }

    // IMO number validation (7 digits, optional)
    if (formData.imo_number && formData.imo_number.trim() !== '') {
      const imoPattern = /^\d{7}$/;
      if (!imoPattern.test(formData.imo_number.trim())) {
        errors.imo_number = 'IMO number must be exactly 7 digits';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingVessel) {
        const result = await updateVessel(editingVessel.id, formData);
        if (result.success) {
          toast.success('Vessel updated successfully');
          setShowModal(false);
          setEditingVessel(null);
          setValidationErrors({});
        } else {
          toast.error(result.error || 'Failed to update vessel');
        }
      } else {
        const result = await createVessel(formData);
        if (result.success) {
          toast.success('Vessel created successfully');
          setShowModal(false);
          setValidationErrors({});
        } else {
          toast.error(result.error || 'Failed to create vessel');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (vesselId) => {
    setIsSubmitting(true);
    try {
      const result = await deleteVessel(vesselId);
      if (result.success) {
        toast.success('Vessel deleted successfully');
        setDeleteConfirm(null);
      } else {
        toast.error(result.error || 'Failed to delete vessel');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        <span className="ml-3 text-slate-600">Loading vessels...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-red-800 font-medium">Failed to load vessels</p>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
        <button
          onClick={refresh}
          className="text-red-600 hover:text-red-800 font-medium text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (vessels.length === 0) {
    return (
      <div className="text-center py-12">
        <Ship className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          No vessels yet
        </h3>
        <p className="text-slate-600 mb-4">
          Your organization has no vessels yet.
        </p>
        {hasEditPermission && (
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Your First Vessel
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">My Vessels</h2>
          <p className="text-sm text-slate-500">
            Manage vessels in your organization
          </p>
        </div>
      </div>

      {/* Vessels Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-medium text-slate-900 flex items-center gap-2">
            <Ship className="h-4 w-4" />
            Fleet Vessels ({vessels.length})
          </h3>
          {hasEditPermission && (
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Add Vessel
            </button>
          )}
        </div>
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Vessel
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                IMO Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Flag State
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Type
              </th>
              {hasEditPermission && (
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {vessels.map((vessel) => (
              <tr key={vessel.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <Ship className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{vessel.name || 'Unnamed Vessel'}</div>
                      <div className="text-xs text-slate-500">ID: {vessel.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                  {vessel.imo_number || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                  {vessel.flag_state || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                  {vessel.vessel_type || 'N/A'}
                </td>
                {hasEditPermission && (
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleOpenEdit(vessel)}
                      className="p-2 text-slate-400 hover:text-cyan-600 transition-colors"
                      title="Edit vessel"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(vessel)}
                      className="p-2 text-slate-400 hover:text-red-600 transition-colors ml-2"
                      title="Delete vessel"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => !isSubmitting && setShowModal(false)}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                {editingVessel ? 'Edit Vessel' : 'Add Vessel'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Vessel Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent ${
                        validationErrors.name ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {validationErrors.name && (
                      <p className="text-red-600 text-sm mt-1">{validationErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      IMO Number
                    </label>
                    <input
                      type="text"
                      value={formData.imo_number}
                      onChange={(e) => setFormData({ ...formData, imo_number: e.target.value })}
                      placeholder="7 digits (optional)"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent ${
                        validationErrors.imo_number ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {validationErrors.imo_number && (
                      <p className="text-red-600 text-sm mt-1">{validationErrors.imo_number}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Flag State
                    </label>
                    <select
                      value={formData.flag_state}
                      onChange={(e) => setFormData({ ...formData, flag_state: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent"
                    >
                      <option value="">Select flag state...</option>
                      {FLAG_STATE_OPTIONS.map(flag => (
                        <option key={flag} value={flag}>{flag}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Vessel Type
                    </label>
                    <select
                      value={formData.vessel_type}
                      onChange={(e) => setFormData({ ...formData, vessel_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent"
                    >
                      <option value="">Select vessel type...</option>
                      {VESSEL_TYPE_OPTIONS.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : (editingVessel ? 'Save Changes' : 'Add Vessel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => !isSubmitting && setDeleteConfirm(null)}
            />

            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-red-600 mb-2">Delete Vessel</h3>
              <p className="text-slate-600 mb-4">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> All data associated with this vessel will be permanently deleted. This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Deleting...' : 'Delete Vessel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselsTab;
